import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { CompaniesService, AllowedCompany } from '../../services/companies.service';
import { AuthRepository, UserRole } from '../../core/auth/auth.repository';

@Component({
  selector: 'app-company-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule
  ],
  template: `
    <!-- Hidden for students -->
    <ng-container *ngIf="isVisible">
      
      <!-- Dropdown for multiple companies or global admin -->
      <div *ngIf="showDropdown" class="company-selector flex items-center">
        <mat-form-field appearance="outline" class="company-dropdown">
          <mat-select 
            [(value)]="selectedCompanyId"
            (selectionChange)="onCompanyChange($event.value)"
            [disabled]="isLoading"
            panelClass="company-select-panel">
            
            <!-- Custom trigger to avoid icon text showing in selected value -->
            <mat-select-trigger>
              <div class="flex items-center">
                <mat-icon class="icon-size-5 mr-2" [class.text-gray-400]="selectedCompanyId === null" [class.text-primary]="selectedCompanyId !== null">
                  {{ selectedCompanyId === null ? 'business' : 'domain' }}
                </mat-icon>
                <span>{{ getSelectedCompanyName() | translate }}</span>
              </div>
            </mat-select-trigger>
            
            <!-- All Companies option (for global admins or users with multiple companies) -->
            <mat-option *ngIf="isGlobalAdmin || allowedCompanies.length > 1" [value]="null">
              <div class="flex items-center">
                <mat-icon class="icon-size-5 mr-2 text-gray-400">business</mat-icon>
                <span>{{ 'All Companies' | translate }}</span>
              </div>
            </mat-option>
            
            <!-- Company options -->
            <mat-option *ngFor="let company of allowedCompanies" [value]="company.id">
              <div class="flex items-center">
                <mat-icon class="icon-size-5 mr-2 text-primary">domain</mat-icon>
                <span>{{ company.name }}</span>
                <mat-icon *ngIf="company.is_primary" class="icon-size-4 ml-2 text-amber-500" [matTooltip]="'Primary company' | translate">star</mat-icon>
              </div>
            </mat-option>
          </mat-select>
          
          <mat-icon matSuffix *ngIf="isLoading">
            <mat-spinner diameter="18"></mat-spinner>
          </mat-icon>
        </mat-form-field>
      </div>

      <!-- Badge for single company (non-clickable) -->
      <div *ngIf="showBadge" class="company-badge flex items-center">
        <div class="flex items-center px-3 py-1.5 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200 rounded-full text-sm font-medium">
          <mat-icon class="icon-size-4 mr-1.5">domain</mat-icon>
          <span class="truncate max-w-[150px]" [matTooltip]="singleCompanyName">
            {{ singleCompanyName }}
          </span>
        </div>
      </div>
    </ng-container>
  `,
  styles: [`
    .company-selector {
      min-width: 260px;
    }
    
    .company-dropdown {
      width: 260px;
    }
    
    :host ::ng-deep .company-dropdown .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }
    
    :host ::ng-deep .company-dropdown .mat-mdc-text-field-wrapper {
      padding: 0 8px;
    }
    
    :host ::ng-deep .company-dropdown .mat-mdc-form-field-infix {
      padding: 8px 0;
      min-height: 36px;
    }

    :host ::ng-deep .company-dropdown .mat-mdc-select-value-text {
      white-space: nowrap;
    }
    
    .company-badge {
      cursor: default;
    }
  `]
})
export class CompanySelectorComponent implements OnInit, OnDestroy {
  @Output() companyChanged = new EventEmitter<number | null>();

  allowedCompanies: AllowedCompany[] = [];
  selectedCompanyId: number | null = null;
  isLoading = false;
  
  // Visibility states
  isVisible = false;
  isGlobalAdmin = false;
  showDropdown = false;
  showBadge = false;
  singleCompanyName = '';

  private _unsubscribeAll = new Subject<void>();

  constructor(
    private companiesService: CompaniesService,
    private authRepository: AuthRepository
  ) {}

  ngOnInit(): void {
    const user = this.authRepository.getUser();
    if (!user) {
      this.isVisible = false;
      return;
    }

    // Students should not see the company selector
    if (user.role === UserRole.Student) {
      this.isVisible = false;
      return;
    }

    this.isVisible = true;
    this.isGlobalAdmin = user.is_global_admin || 
                          user.role === UserRole.SuperUser || 
                          user.role === UserRole.SysAdmin;

    // Set user role for permission checks
    this.companiesService.setUserRole(user.role);

    // Set allowed companies from user data
    if (user.allowed_companies && user.allowed_companies.length > 0) {
      this.allowedCompanies = user.allowed_companies;
      this.companiesService.setAllowedCompanies(user.allowed_companies, this.isGlobalAdmin);
    } else {
      // Ensure service is initialized even with empty companies
      this.companiesService.setAllowedCompanies([], this.isGlobalAdmin);
    }

    // Set allowed courses for supervisors
    if (user.allowed_courses && user.allowed_courses.length > 0) {
      this.companiesService.setAllowedCourses(user.allowed_courses);
    }

    // Determine display mode
    this.updateDisplayMode();

    // Subscribe to selected company
    this.companiesService.selectedCompanyId$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(companyId => {
        this.selectedCompanyId = companyId;
      });

    // Subscribe to loading state
    this.companiesService.isLoading$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    // For global admins, load all companies
    if (this.isGlobalAdmin) {
      this.companiesService.loadCompanies()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(companies => {
          // Merge with allowed companies for display
          if (companies.length > 0) {
            this.allowedCompanies = companies.map(c => ({
              id: c.id,
              name: c.name,
              is_primary: false,
              can_manage: true
            }));
          }
        });
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private updateDisplayMode(): void {
    if (!this.isVisible) {
      this.showDropdown = false;
      this.showBadge = false;
      return;
    }

    // Global admins always get dropdown
    if (this.isGlobalAdmin) {
      this.showDropdown = true;
      this.showBadge = false;
      return;
    }

    // Multiple companies = dropdown
    if (this.allowedCompanies.length > 1) {
      this.showDropdown = true;
      this.showBadge = false;
      return;
    }

    // Single company = badge
    if (this.allowedCompanies.length === 1) {
      this.showDropdown = false;
      this.showBadge = true;
      this.singleCompanyName = this.allowedCompanies[0].name;
      
      // Auto-select the single company
      if (this.selectedCompanyId === null) {
        this.companiesService.selectCompany(this.allowedCompanies[0].id);
      }
      return;
    }

    // No companies - could be a supervisor with course-only assignments
    // or a misconfigured user. Hide the selector entirely.
    this.showDropdown = false;
    this.showBadge = false;
    // For supervisors with 0 companies but with courses, the backend handles 
    // filtering by course assignments when no company is selected (companyId=null)
  }

  onCompanyChange(companyId: number | null): void {
    this.companiesService.selectCompany(companyId);
    this.companyChanged.emit(companyId);
  }

  getSelectedCompanyName(): string {
    if (this.selectedCompanyId === null) {
      return 'All Companies';
    }
    const company = this.allowedCompanies.find(c => c.id === this.selectedCompanyId);
    return company?.name || 'Select Company';
  }
}
